"use client";
import { Box, Button, Popover, Typography } from "@mui/material";
import React, { memo, useCallback, useRef } from "react";
import SubscribeButton from "../button/SubscribeButton2";

// hook để chuyển đổi giữa chế độ fullscreen và chế độ bình thường
import { useFullscreen } from "../fullscreen";
import ExitFullScreen from "../fullscreen/ExitFullscreen";

import { APP_ROUTES } from "@/constants/routes";
import { usePrice } from "@/helper/usePrice";
import { useSubscription } from "@/helper/useSubscription";
import useDeviceDetect from "@/hooks/useMobile";
import {
  setBookmarkState,
  setPreviousPage,
  setReload,
  setSavedVisuals,
} from "@/lib/features/powerBiSlice";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import { ConfigData } from "@/models/config";
import { OPTIONS_SUBSCRIPTION, subscription } from "@/models/subscription";
import { User } from "@/models/user";
import PowerBiServices from "@/services/admin/powerBiServices";
import PowerBiService from "@/services/main/powerBiService";
import BookmarkAddedOutlinedIcon from "@mui/icons-material/BookmarkAddedOutlined";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import * as pbi from "powerbi-client";
import BookmarkDashboard from "./BookmarkDashboard";
import BookmarkDashboardMobile from "./BookmarkDashboardMobile";
import SaveDashboardModal from "./PopupSaveSnapshot";
const Filter = dynamic(() => import("./filterBar"), {
  ssr: false,
});
const PowerBIContainer = dynamic(() => import("./powerBi/PowerBIContainer"), {
  ssr: false,
});
const FilterComponent = dynamic(() => import("./filter"), {
  ssr: false,
});
const PopupExport = dynamic(() => import("./PopupExport"), {
  ssr: false,
});

interface InteractiveDashboardProps {
  minSubscription: subscription | null;
  isLoading?: boolean;
  workspaceId?: string;
  embedUrl?: string;
  powerBiId?: string;
  reportId?: string | number;
  bookmarkState?: string;
  titleReport?: string;
  slug?: string;
  token?: string;
  bookmarkCount?: number;
  isLoggedIn?: boolean;
  isMobile?: boolean;
  locale: string;
  user?: User | null;
}

const InteractiveDashboard = ({
  isLoading,
  embedUrl,
  powerBiId,
  workspaceId,
  reportId,
  bookmarkState,
  titleReport,
  bookmarkCount,
  minSubscription,
  token,
  slug,
  isLoggedIn,
  isMobile,
  locale,
  user,
}: InteractiveDashboardProps) => {
  const getPowerBi = useRef<{
    getPowerBi: () => pbi.Report;
  } | null>(null);
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const config = queryClient.getQueryData<ConfigData>(["config"]);
  const refFilter = useRef<{ resetFilter: () => void }>(null);
  // const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [interactiveDashboard, setInteractiveDashboard] = React.useState({
    isFilterOpen: false,
    isSaveOpen: false,
    isExportOpen: false,
  });
  const { currencyLocal: currency } = useAppSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);
  const id = open ? "bookmark-popover" : undefined;
  const router = useRouter();
  const { hasPermission } = useSubscription(user?.license);
  const dispatch = useAppDispatch();
  const isMobile2 = useDeviceDetect();
  const { currentPage, visuals } = useAppSelector((state) => state.powerBi);
  const { getCostCurrency } = usePrice();
  const { data: accessToken, isLoading: isLoadingAccessToken } = useQuery({
    queryKey: ["get-access-token-powerbi", workspaceId, powerBiId],
    queryFn: async () =>
      await PowerBiServices.getTokenPowerBi(workspaceId, powerBiId, {
        accessLevel: "view",
      }),
    enabled: !!workspaceId && !!powerBiId,
    refetchOnWindowFocus: false,
  });
  const interactiveContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, toggleFullscreen] = useFullscreen(
    interactiveContainerRef
  );
  const handleFilterClick = () => {
    return setInteractiveDashboard((prev) => ({
      ...prev,
      isFilterOpen: !prev.isFilterOpen,
    }));
  };
  const handleSaveClick = () => {
    return setInteractiveDashboard((prev) => ({
      ...prev,
      isSaveOpen: !prev.isSaveOpen,
    }));
  };
  const handleExportClick = () => {
    return setInteractiveDashboard((prev) => ({
      ...prev,
      isExportOpen: !prev.isExportOpen,
    }));
  };

  const handleRedierectToPaymentPricing = useCallback(() => {
    if (isLoggedIn) {
      router.push(
        APP_ROUTES(locale).PAYMENT.SUBSCRIPTION.ROOT.to.replace(
          ":id",
          minSubscription?.id.toString() ?? ""
        )
      );
    } else {
      const currentUrl =
        typeof window !== "undefined"
          ? window.location.pathname +
            window.location.search +
            window.location.hash
          : "";
      router.push(
        `${APP_ROUTES(locale).REGISTER.to}?type=plan&redirect=${encodeURIComponent(currentUrl)}`
      );
    }
  }, [isLoggedIn, router, minSubscription?.id, locale]);

  const handleChangeBookmark = useCallback(
    async (state: string) => {
      if (getPowerBi.current && currentPage?.pageId && visuals) {
        dispatch(setBookmarkState(state));
        const report = getPowerBi.current.getPowerBi();
        report.bookmarksManager?.applyState(state);
      }
    },
    [getPowerBi, visuals, currentPage]
  );
  const { data: bookmarks, isLoading: isLoadingBookmarks } = useQuery({
    queryKey: ["bookmark-dashboard", reportId],
    queryFn: async () => {
      if (!reportId) return [];
      const result = await PowerBiService.getDashboardByReport(reportId);
      return result?.data;
    },
    refetchOnWindowFocus: false,
    enabled: !!reportId && !!user,
  });
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleReloadClick = async () => {
    if (getPowerBi.current) {
      try {
        dispatch(setPreviousPage(currentPage));
        dispatch(setReload(true));
        dispatch(setSavedVisuals(null));
        await getPowerBi.current?.getPowerBi().reload();
      } catch (errors) {
        // Handle error silently
      }
    }
  };
  return (
    <Box className="!w-full !mx-auto !max-w-full xl:!max-w-[1440px]">
      {/* Header Section */}
      <Box className="!px-4 lg:!px-[36px] !pt-3 lg:!pt-[24px] mb-2 lg:!mb-6 flex justify-between">
        <Typography
          variant="h2"
          className="!font-semibold !text-xl !sm:text-2xl/8 !tracking-[-0.03em] !text-eerie_black !m-0"
          translation-key="report_detail_interactive_dashboard_title"
        >
          {t("report_detail_interactive_dashboard_title")}
        </Typography>
        {!bookmarkState ? (
          bookmarks && accessToken ? (
            <>
              <Button
                aria-describedby={id}
                className="rounded-[4px] p-[8px] !bg-[#ECF8BF] flex items-center gap-2 cursor-pointer active:scale-[0.98] transition-transform duration-200 ease-in-out report-add-bookmark-button"
                onClick={handleClick}
              >
                <BookmarkAddedOutlinedIcon className="!text-black" />
                <Typography className="!font-[500] !text-[14px] !leading-6 !text-cimigo-green-dark-2">
                  {t("interactive_dashboard_bookmark_count", {
                    count: bookmarks?.length || 0,
                  })}
                </Typography>
              </Button>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                sx={{
                  "& .MuiPopover-paper": {
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    width: isMobile || isMobile2 ? "90%" : "430px",
                    maxWidth: "100%",
                    maxHeight: "50vh",
                    overflowY: "auto",
                    borderRadius: "8px",
                    paddingTop: "20px !important",
                    padding: "18px",
                    height: "100%",
                  },
                }}
              >
                {isMobile || isMobile2 ? (
                  <BookmarkDashboardMobile
                    reportId={reportId}
                    handleChangeBookmark={handleChangeBookmark}
                    data={bookmarks}
                    isLoading={isLoadingBookmarks}
                  />
                ) : (
                  <BookmarkDashboard
                    reportId={reportId}
                    handleChangeBookmark={handleChangeBookmark}
                    data={bookmarks}
                    isLoading={isLoadingBookmarks}
                  />
                )}
              </Popover>
            </>
          ) : null
        ) : null}
      </Box>
      {/* Main Content Section */}
      <Box
        className={cn("!py-3 sm:!p-4 md:!p-6", {
          "!hidden": hasPermission(OPTIONS_SUBSCRIPTION.INTERACTIVE),
        })}
      >
        <Box className="!p-4 sm:!p-5 lg:!p-6 !rounded !bg-ciBlue_light_4 flex flex-col lg:flex-row lg:justify-between lg:items-center !gap-4 lg:!gap-6">
          {/* Left Content */}
          <Box className="flex flex-col !gap-2 flex-shrink-0">
            <Typography
              variant="h3"
              className="!font-semibold !text-lg sm:!text-xl lg:!text-[22px]/8 !tracking-[-0.03em] !text-ciBlue_900 !m-0"
              translation-key="report_detail_interactive_dashboard_viewing_total"
            >
              {t("report_detail_interactive_dashboard_viewing_total")}
            </Typography>

            <Box className="!mt-1">
              <Typography
                variant="subtitle1"
                className="!font-semibold !text-base/6 !tracking-[-0.03em] !text-eerie_black !m-0"
                translation-key="report_detail_interactive_dashboard_preview_label"
              >
                {t("report_detail_interactive_dashboard_preview_label")}
              </Typography>
              <Typography
                variant="body2"
                className="!font-medium !text-sm lg:!text-base/6 !tracking-[-0.03em] !text-eerie_black !m-0 !mt-1"
                translation-key="report_detail_interactive_dashboard_preview_description"
              >
                {t.rich(
                  "report_detail_interactive_dashboard_preview_description",
                  {
                    br: () => <br />,
                  }
                )}
              </Typography>
            </Box>
          </Box>

          {/* Right Content - Subscribe Button */}
          <Box className="flex flex-col items-center !gap-2 lg:!min-w-[320px] xl:!min-w-[400px] 2xl:!min-w-[472px]">
            <SubscribeButton
              onClick={() => handleRedierectToPaymentPricing()}
            />
            <Typography
              variant="body2"
              className="!font-medium !text-sm xl:!text-base !text-ciBlue_900 !text-center !m-0"
              translation-key="report_detail_interactive_dashboard_price"
            >
              {t("report_detail_interactive_dashboard_price", {
                amount: getCostCurrency(
                  Number(minSubscription?.amountShow) || 0,
                  user?.currency || currency || "USD",
                  config?.usdToVND,
                  config?.idrToVND
                ).show,
              })}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Filter Bar */}
      <Box
        ref={interactiveContainerRef}
        className={isFullscreen ? "fullscreen-fix-overflow" : ""}
      >
        <Box className="">
          {accessToken ? (
            <Filter
              handleReset={refFilter.current?.resetFilter}
              powerBi={getPowerBi.current?.getPowerBi()}
              handleReloadClick={handleReloadClick}
              isFullscreen={isFullscreen}
              bookmarkState={bookmarkState}
              handleFilterClick={handleFilterClick}
              handleFullscreenClick={toggleFullscreen}
              handleSaveClick={handleSaveClick}
              isFilterOpen={interactiveDashboard.isFilterOpen}
              isSaveOpen={interactiveDashboard.isSaveOpen}
              handleExportClick={handleExportClick}
              token={token}
              isLoggedIn={isLoggedIn}
              isExportOpen={interactiveDashboard.isExportOpen}
            />
          ) : null}
          <FilterComponent
            // key={currentPage?.pageId}
            ref={refFilter}
            isFullscreen={isFullscreen}
            isMobile={isMobile || isMobile2}
            isFilterOpen={interactiveDashboard.isFilterOpen}
            onClose={handleFilterClick}
            powerBi={getPowerBi.current?.getPowerBi()}
          />
          {interactiveDashboard.isSaveOpen && (
            <SaveDashboardModal
              locale={locale}
              powerBi={getPowerBi.current?.getPowerBi()}
              titleReport={titleReport}
              open={interactiveDashboard.isSaveOpen}
              onClose={handleSaveClick}
              reportId={reportId}
            />
          )}
        </Box>

        {/* Skeleton Loading Section */}
        <Box
          sx={{
            paddingBottom: isFullscreen ? "165px" : "0", // Padding cho filter bar khi fullscreen
          }}
          className={`flex-1 ${isFullscreen ? "!w-full !h-full" : "!h-auto md:!h-[100vh]"} flex flex-col bg-[#F0F2F9] relative`}
        >
          {interactiveDashboard.isExportOpen && (
            <PopupExport
              isOpen={interactiveDashboard.isExportOpen}
              isMobile={isMobile}
              reportId={reportId}
              reportName={titleReport}
              powerBiId={powerBiId}
              onClose={handleExportClick}
              workspaceId={workspaceId}
              slug={slug}
              token={token}
              powerBi={getPowerBi.current?.getPowerBi()}
            />
          )}
          {/* nội dung chinhs*/}
          {!isLoading && (
            <>
              <Box
                className={`
              flex-1
              flex
              flex-col
              !thin-scrollbar
                ${isFullscreen ? "!h-full !overflow-visible" : "!h-auto !overflow-auto"}
              !min-h-[60vh]
              md:!min-h-screen
              !gap-6
              sm:!gap-12
              ${isFullscreen ? "!pb-[75px]" : ""}
            `}
              >
                <Box
                  className={`!relative !w-full !h-[100vh] !max-h-full !flex !justify-center `}
                >
                  <PowerBIContainer
                    handleChangeBookmark={handleChangeBookmark}
                    reportId={reportId}
                    bookmarks={bookmarks}
                    isLoadingBookmarks={isLoadingBookmarks}
                    bookmarkState={bookmarkState}
                    ref={getPowerBi}
                    isLoading={isLoadingAccessToken}
                    embedUrl={embedUrl}
                    powerBiId={powerBiId}
                    workspaceId={workspaceId}
                    accessToken={accessToken?.token}
                  />
                </Box>
                <div
                  className="absolute inset-0 pointer-events-none z-10"
                  style={{
                    boxShadow: `
                    inset 0 0 15px rgba(0, 0, 0, 0.08),
                    inset 0 0 30px rgba(0, 0, 0, 0.04)
                  `,
                    borderRadius: "4px",
                  }}
                />
              </Box>

              {/* Fullscreen Exit Button */}
              {isFullscreen && (
                <ExitFullScreen onExitFullscreen={toggleFullscreen} />
              )}
            </>
          )}
        </Box>
      </Box>
      {/* đổ bóng cho phần content */}
      <Box className="!card-glass-effect-2 !bg-opacity-0"></Box>
    </Box>
  );
};

export default memo(InteractiveDashboard);
